import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { authAPI, type LegacyUser } from '../services/api';

interface AuthContextValue {
  user: LegacyUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<LegacyUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  async function refresh() {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      return;
    }
    const r = await authAPI.verify();
    if (r.status === 'success' && r.data) {
      setUser(r.data.user);
    } else {
      localStorage.removeItem('token');
      setUser(null);
    }
  }

  async function login(email: string, password: string) {
    const r = await authAPI.login(email, password);
    if (r.status === 'success' && r.token && r.data) {
      localStorage.setItem('token', r.token);
      setUser(r.data.user);
      return { ok: true };
    }
    return { ok: false, error: r.message ?? 'Falha no login' };
  }

  async function logout() {
    await authAPI.logout();
    localStorage.removeItem('token');
    sessionStorage.removeItem('activePage');
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return ctx;
}
